import { FiMail, FiMessageCircle, FiPhoneCall } from "react-icons/fi";

import { Tooltip } from "@/components/ui";

import { useCommunication } from "@/contexts/chat-provider";

import {
  formatDate,
  formatStaffName,
  formatToHumanReadable,
} from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

import { EventAttributionSourceType } from "@/types/enums";
import type { EventAttributionSummary } from "@/types/event";

interface AttributionLineProps {
  attribution?: EventAttributionSummary | null;
  patientId?: string | null;
  className?: string;
  compact?: boolean;
}

export function formatAttributionSource(
  sourceType?: EventAttributionSourceType | null,
) {
  return sourceType ? formatToHumanReadable(sourceType) : "Unknown source";
}

function AttributionSourceIcon({
  sourceType,
  className,
}: {
  sourceType?: EventAttributionSourceType | null;
  className?: string;
}) {
  const iconClassName = cx("h-3 w-3 shrink-0 text-muted-foreground", className);

  switch (sourceType) {
    case EventAttributionSourceType.CALL:
      return <FiPhoneCall className={iconClassName} />;
    case EventAttributionSourceType.EMAIL:
      return <FiMail className={iconClassName} />;
    case EventAttributionSourceType.SMS:
    default:
      return <FiMessageCircle className={iconClassName} />;
  }
}

export function AttributionLine({
  attribution,
  patientId,
  className,
  compact = true,
}: AttributionLineProps) {
  const { openChat } = useCommunication();

  if (!attribution) return null;

  const staffName = attribution.staffId
    ? formatStaffName(attribution.staffId)
    : "Unknown staff";
  const firstName = staffName.split(" ")[0] || staffName;
  const sourceLabel = formatAttributionSource(attribution.sourceType);
  const label = compact
    ? `Attributed to ${firstName}`
    : `Attributed to ${staffName}`;
  const canOpenChat = Boolean(patientId);
  const attributedAt = attribution.attributedAt
    ? formatDate(attribution.attributedAt)
    : null;

  const content = (
    <span className="inline-flex items-center gap-2">
      <AttributionSourceIcon sourceType={attribution.sourceType} />
      <span>{label}</span>
    </span>
  );

  const tooltipContent = (
    <div className="space-y-1 text-caption">
      <div className="font-medium text-foreground">Attribution</div>
      <div className="text-foreground-secondary">Staff: {staffName}</div>
      <div className="text-foreground-secondary">Source: {sourceLabel}</div>
      {attributedAt && (
        <div className="text-foreground-secondary">
          Last Outbound Contact: {attributedAt}
        </div>
      )}
      {canOpenChat && (
        <div className="text-primary">Click to view conversation</div>
      )}
    </div>
  );

  if (!canOpenChat) {
    return (
      <Tooltip
        content={tooltipContent}
        triggerAsChild
        triggerOnlyHover
        variant="light"
      >
        <span
          className={cx(
            "inline-flex items-center text-caption leading-snug text-foreground-secondary",
            className,
          )}
        >
          {content}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={tooltipContent}
      triggerAsChild
      triggerOnlyHover
      variant="light"
    >
      <button
        type="button"
        className={cx(
          "inline-flex w-fit cursor-pointer items-center text-caption leading-snug text-foreground-secondary hover:text-foreground",
          className,
        )}
        onClick={() => {
          if (patientId) {
            openChat(patientId);
          }
        }}
      >
        {content}
      </button>
    </Tooltip>
  );
}
